import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Diamond } from "lucide-react";

const DAY = 86400000;
const ROW = 48; // px per WBS row (h-12)

const SCALES = {
  week: { label: "Weekly", px: 11 },
  month: { label: "Monthly", px: 3.4 },
  year: { label: "Yearly", px: 1.1 },
};
const SCALE_KEY = "okn-gantt-scale-v1";

const toDate = (value) => (value ? new Date(`${value}T00:00:00`) : null);
const fmt = (value) =>
  value ? toDate(value).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—";

const KIND_COLOR = {
  design: "#8B5CF6",
  bim: "#3B82F6",
  procurement: "#F59E0B",
  production: "#6366F1",
  qaqc: "#10B981",
  logistics: "#06B6D4",
  site: "#EC4899",
  custom: "#64748B",
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const startOfYear = (d) => new Date(d.getFullYear(), 0, 1);

/** Builds the two-level timescale header for the visible window. */
const useScale = (rows, projectStart, projectFinish, mode, viewWidth) =>
  useMemo(() => {
    const dates = rows
      .flatMap((r) => [r.start, r.finish, r.baseline_start, r.baseline_finish])
      .filter(Boolean)
      .concat([projectStart, projectFinish].filter(Boolean));
    if (dates.length === 0) return null;
    const min = new Date(Math.min(...dates.map((d) => toDate(d).getTime())));
    const max = new Date(Math.max(...dates.map((d) => toDate(d).getTime())));
    min.setDate(min.getDate() - ((min.getDay() + 6) % 7) - 3);
    max.setDate(max.getDate() + 10);
    const days = Math.max(Math.round((max - min) / DAY), 14);
    const px =
      mode !== "week" && viewWidth > 0 ? viewWidth / days : SCALES[mode].px;
    const end = new Date(min.getTime() + days * DAY);
    const at = (d) => (d - min) / DAY * px;

    const build = (first, next, label) => {
      const cells = [];
      let cursor = first(min);
      while (cursor < end) {
        const after = next(cursor);
        const left = Math.max(at(cursor), 0);
        cells.push({
          key: cursor.getTime(),
          left,
          width: Math.min(at(after), days * px) - left,
          label: label(cursor),
        });
        cursor = after;
      }
      return cells;
    };

    const addDays = (n) => (d) => new Date(d.getTime() + n * DAY);
    const addMonths = (n) => (d) => new Date(d.getFullYear(), d.getMonth() + n, 1);

    let minor;
    let major;
    if (mode === "week") {
      minor = build(
        (d) => new Date(d.getTime()),
        addDays(7),
        (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" }),
      );
      major = build(startOfMonth, addMonths(1), (d) =>
        d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
      );
    } else if (mode === "month") {
      minor = build(startOfMonth, addMonths(1), (d) =>
        d.toLocaleDateString("en-GB", { month: "short" }),
      );
      major = build(startOfYear, addMonths(12), (d) => String(d.getFullYear()));
    } else {
      minor = build(startOfQuarter, addMonths(3), (d) => `Q${Math.floor(d.getMonth() / 3) + 1}`);
      major = build(startOfYear, addMonths(12), (d) => String(d.getFullYear()));
    }

    return { min, days, px, width: days * px, minor, major };
  }, [rows, projectStart, projectFinish, mode, viewWidth]);

const Bar = ({ scale, row, color }) => {
  const start = toDate(row.start);
  const finish = toDate(row.finish);
  if (!start || !finish) return null;
  const left = ((start - scale.min) / DAY) * scale.px;
  const width = Math.max((((finish - start) / DAY) + 1) * scale.px, 4);
  const baseStart = toDate(row.baseline_start);
  const baseFinish = toDate(row.baseline_finish);
  const shifted =
    baseStart && baseFinish && (row.baseline_start !== row.start || row.baseline_finish !== row.finish);

  if (row.milestone) {
    return (
      <>
        <div
          data-testid={`gantt-milestone-${row.id}`}
          className="absolute top-1/2 -translate-y-1/2"
          style={{ left: left - 8 }}
          title={`${row.name} · ${fmt(row.start)}`}
        >
          <Diamond size={16} color={color} fill={color} />
        </div>
        {row.late ? (
          <AlertTriangle
            size={13}
            className="absolute top-1/2 -translate-y-1/2 text-red-500"
            style={{ left: left + 12 }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {shifted ? (
        <div
          data-testid={`gantt-baseline-${row.id}`}
          className="absolute top-1 h-1.5 rounded-full bg-slate-300"
          style={{
            left: ((baseStart - scale.min) / DAY) * scale.px,
            width: Math.max((((baseFinish - baseStart) / DAY) + 1) * scale.px, 4),
          }}
          title={`Baseline ${fmt(row.baseline_start)} → ${fmt(row.baseline_finish)}`}
        />
      ) : null}
      <div
        data-testid={`gantt-bar-${row.id}`}
        className="absolute top-1/2 h-5 -translate-y-1/2 overflow-hidden rounded-md"
        style={{ left, width, backgroundColor: `${color}33`, border: `1px solid ${color}` }}
        title={`${row.name} · ${fmt(row.start)} → ${fmt(row.finish)} · ${Math.round(row.progress)}%`}
      >
        <div
          className="h-full"
          style={{ width: `${Math.min(row.progress, 100)}%`, backgroundColor: color }}
        />
      </div>
      {row.late ? (
        <AlertTriangle
          size={13}
          data-testid={`gantt-late-${row.id}`}
          className="absolute top-1/2 -translate-y-1/2 text-red-500"
          style={{ left: left + width + 6 }}
        />
      ) : null}
    </>
  );
};

/** MS-Project style timeline: WBS rows on the left, scaled bars on the right. */
export function GanttChart({ phases, activities, projectStart, projectFinish, onEditActivity, canPlan }) {
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(SCALE_KEY);
      return SCALES[saved] ? saved : "week";
    } catch (error) {
      console.debug("gantt scale preference unavailable", error);
      return "week";
    }
  });

  const [viewWidth, setViewWidth] = useState(0);

  const pickMode = useCallback((next) => {
    setMode(next);
    try {
      localStorage.setItem(SCALE_KEY, next);
    } catch (error) {
      console.debug("gantt scale preference not saved", error);
    }
  }, []);

  const rows = useMemo(() => {
    const out = [];
    phases.forEach((phase) => {
      out.push({ ...phase, type: "phase", progress: phase.overall });
      activities
        .filter((a) => a.phase_id === phase.id)
        .forEach((a) => out.push({ ...a, type: "activity" }));
    });
    return out;
  }, [phases, activities]);

  const scale = useScale(rows, projectStart, projectFinish, mode, viewWidth);
  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const timeline = useRef(null);

  useEffect(() => {
    const node = timeline.current;
    if (!node) return undefined;
    const measure = () => setViewWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [rows.length]);

  const links = useMemo(() => {
    if (!scale) return [];
    const index = new Map(rows.map((row, i) => [`${row.type}-${row.id}`, i]));
    const out = [];
    rows.forEach((row) => {
      if (row.type !== "activity") return;
      (row.depends_on || []).forEach((depId) => {
        const from = rows.find((r) => r.type === "activity" && r.id === depId);
        if (!from || !from.finish || !row.start) return;
        const fromRow = index.get(`activity-${from.id}`);
        const toRow = index.get(`activity-${row.id}`);
        const type = (row.dependency_types || {})[depId] || "FS";
        const lag = Number((row.dependency_lags || {})[depId] || 0);
        const at = (iso, endEdge) =>
          (((toDate(iso) - scale.min) / DAY) + (endEdge ? 1 : 0)) * scale.px;
        const fromEnd = type === "FS" || type === "FF";
        const toEnd = type === "FF" || type === "SF";
        const fromDate = fromEnd ? from.finish : from.start;
        const toDateIso = toEnd ? row.finish : row.start;
        if (!fromDate || !toDateIso) return;
        const required = new Date(
          toDate(fromDate).getTime() + (lag + (type === "FS" ? 1 : 0)) * DAY
        );
        const violated = toDate(toDateIso) < required;
        out.push({
          key: `${from.id}-${row.id}`,
          type,
          label: lag === 0 ? type : `${type}${lag > 0 ? "+" : "−"}${Math.abs(lag)}`,
          x1: at(fromDate, fromEnd),
          y1: fromRow * ROW + ROW / 2,
          x2: at(toDateIso, toEnd),
          y2: toRow * ROW + ROW / 2,
          violated,
        });
      });
    });
    return out;
  }, [rows, scale]);

  useEffect(() => {
    if (!scale || !timeline.current) return;
    const left = ((today - scale.min) / DAY) * scale.px;
    timeline.current.scrollLeft = Math.max(left - 220, 0);
  }, [scale, today]);

  if (!scale || rows.length === 0)
    return (
      <p
        data-testid="gantt-empty"
        className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-500"
      >
        No phases yet. Create a phase from saved filter presets to build the schedule.
      </p>
    );

  const todayLeft = ((today - scale.min) / DAY) * scale.px;

  return (
    <div
      data-testid="gantt-chart"
      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <span className="mono text-[10px] uppercase tracking-wider text-slate-500">Timescale</span>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {Object.entries(SCALES).map(([key, cfg]) => (
            <button
              key={key}
              data-testid={`gantt-scale-${key}`}
              onClick={() => pickMode(key)}
              className={`mono rounded-md px-2.5 py-1 text-[10px] uppercase tracking-wider transition-colors ${
                mode === key
                  ? "bg-white text-cyan-700 shadow-sm ring-1 ring-cyan-200"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex">
        <div className="w-[150px] shrink-0 border-r border-slate-200 sm:w-[240px] lg:w-[320px]">
          <div className="mono sticky top-0 z-30 flex h-16 items-end border-b border-slate-200 bg-slate-50 px-3 pb-2 text-[10px] uppercase tracking-wider text-slate-500">
            Activity / WBS
          </div>
          {rows.map((row) => (
            <button
              key={`${row.type}-${row.id}`}
              data-testid={`wbs-${row.type}-${row.id}`}
              onClick={() => row.type === "activity" && canPlan && onEditActivity(row)}
              className={`flex h-12 w-full items-center justify-between gap-2 border-b border-slate-100 px-3 text-left ${
                row.type === "phase" ? "bg-slate-50/70" : "hover:bg-cyan-50/40"
              }`}
            >
              <span className="min-w-0">
                <span
                  className={`block truncate text-[12px] ${
                    row.type === "phase"
                      ? "font-semibold text-slate-900"
                      : "pl-3 text-slate-700"
                  }`}
                >
                  {row.type === "activity" ? "↳ " : ""}
                  {row.name}
                </span>
                <span className="mono block truncate text-[9.5px] uppercase tracking-wider text-slate-400">
                  {row.type === "phase"
                    ? `${row.panel_count} panels · ${(row.preset_names || []).join(", ") || "no preset"}`
                    : `${row.kind_label}${
                        row.panels_total ? ` · ${row.panels_done}/${row.panels_total} panels` : " · manual"
                      }`}
                </span>
              </span>
              <span
                className={`mono shrink-0 text-[11px] ${
                  row.late ? "text-red-600" : "text-slate-600"
                }`}
              >
                {Math.round(row.progress)}%
              </span>
            </button>
          ))}
        </div>

        <div className="okn-scroll min-w-0 flex-1 overflow-x-auto" ref={timeline}>
          <div style={{ width: scale.width }}>
            <div
              data-testid="gantt-timescale"
              className="sticky top-0 z-30 border-b border-slate-200 bg-slate-50"
            >
              <div className="relative h-8 border-b border-slate-200">
                {scale.major.map((cell) => (
                  <div
                    key={cell.key}
                    data-testid={`gantt-major-${cell.key}`}
                    className="mono absolute bottom-1 flex h-5 items-center overflow-hidden whitespace-nowrap border-l border-slate-300 pl-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600"
                    style={{ left: cell.left, width: cell.width }}
                  >
                    {cell.width > 26 ? cell.label : ""}
                  </div>
                ))}
              </div>
              <div className="relative h-8">
                {scale.minor.map((cell) => (
                  <div
                    key={cell.key}
                    data-testid={`gantt-minor-${cell.key}`}
                    className="mono absolute bottom-1 flex h-5 items-center overflow-hidden whitespace-nowrap border-l border-slate-200 pl-1 text-[9.5px] uppercase tracking-wider text-slate-500"
                    style={{ left: cell.left, width: cell.width }}
                  >
                    {cell.width > 22 ? cell.label : ""}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <svg
                data-testid="gantt-dependencies"
                className="pointer-events-none absolute left-0 top-0 z-20"
                width={scale.width}
                height={rows.length * ROW}
              >
                {links.map((link) => (
                  <g key={link.key} data-testid={`gantt-link-${link.key}`}>
                    <polyline
                      points={`${link.x1},${link.y1} ${link.x1 + 8},${link.y1} ${link.x1 + 8},${link.y2} ${link.x2},${link.y2}`}
                      fill="none"
                      stroke={link.violated ? "#EF4444" : "#94A3B8"}
                      strokeWidth="1.2"
                      strokeDasharray={link.violated ? "4 3" : undefined}
                      markerEnd="url(#gantt-arrow)"
                    />
                    <text
                      x={link.x1 + 11}
                      y={(link.y1 + link.y2) / 2 - 2}
                      fill={link.violated ? "#EF4444" : "#64748B"}
                      fontSize="8"
                      fontFamily="monospace"
                    >
                      {link.label}
                    </text>
                  </g>
                ))}
                <defs>
                  <marker
                    id="gantt-arrow"
                    markerWidth="6"
                    markerHeight="6"
                    refX="5"
                    refY="3"
                    orient="auto"
                  >
                    <path d="M0,0 L6,3 L0,6 Z" fill="#94A3B8" />
                  </marker>
                </defs>
              </svg>
              {todayLeft >= 0 && todayLeft <= scale.width ? (
                <div
                  data-testid="gantt-today-line"
                  className="pointer-events-none absolute top-0 z-10 h-full w-px bg-red-500/70"
                  style={{ left: todayLeft }}
                />
              ) : null}
              {rows.map((row) => (
                <div
                  key={`bar-${row.type}-${row.id}`}
                  className={`relative h-12 border-b border-slate-100 ${
                    row.type === "phase" ? "bg-slate-50/70" : ""
                  }`}
                >
                  {scale.minor.map((cell) => (
                    <div
                      key={cell.key}
                      className="absolute top-0 h-full border-l border-slate-100"
                      style={{ left: cell.left }}
                    />
                  ))}
                  <Bar
                    scale={scale}
                    row={row}
                    color={row.type === "phase" ? "#0E7490" : KIND_COLOR[row.kind] || "#64748B"}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
